/**
 * PickupLocationSelectionPage — citizen self-service pickup-location redirect.
 *
 * Sibling of F-076 (address-change). Lets a citizen change an in-flight
 * parcel's destination from "home delivery" to "pickup at a NIPOST outlet".
 * Calls
 *
 *   POST /v1/mail-items/:trackingNumber/pickup-redirect  { outletId }
 *
 * on service-mail. The outlet picker reads the live directory via
 * `listOutlets()` (service-customer GET /v1/outlets — public).
 *
 * Flow:
 *   Step 1: enter tracking number.
 *   Step 2: pick an outlet — searchable list filtered by name/state.
 *   Step 3: confirm — POST + render success card with outlet name + address.
 *
 * Error handling:
 *   - 404 — tracking number / outlet not found, or caller is not the owner.
 *   - 409 — parcel is already DELIVERED / in a terminal status.
 *   - Other — generic network / server error.
 */
import { useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  Loader2,
  MapPin,
  Search,
  XCircle,
} from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import { listOutlets, type Outlet } from '../../lib/api/outlets';
import {
  requestPickupRedirect,
  type PickupRedirectResponse,
} from '../../lib/api/pickup-redirect';
import { PageHeader } from './PortalUI';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; result: PickupRedirectResponse }
  | { kind: 'error'; message: string };

export function PickupLocationSelectionPage(): JSX.Element {
  const [tracking, setTracking] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('');
  const [outlets, setOutlets] = useState<readonly Outlet[]>([]);
  const [outletsLoading, setOutletsLoading] = useState<boolean>(true);
  const [outletsError, setOutletsError] = useState<string | null>(null);
  const [selectedOutletId, setSelectedOutletId] = useState<string>('');
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });

  // Load all outlets on mount (small dataset; pageSize=200 covers everything).
  useEffect(() => {
    let cancelled = false;
    setOutletsLoading(true);
    void listOutlets({ pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        setOutlets(res.items);
        setOutletsError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setOutletsError(
          err instanceof Error ? err.message : 'Failed to load outlets',
        );
      })
      .finally(() => {
        if (!cancelled) setOutletsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Build a sorted unique state list for the optional state filter.
  const allStates = useMemo<readonly string[]>(() => {
    const set = new Set<string>();
    for (const o of outlets) set.add(o.state);
    return Array.from(set).sort();
  }, [outlets]);

  const filtered = useMemo<readonly Outlet[]>(() => {
    const q = search.trim().toLowerCase();
    return outlets.filter((o) => {
      if (stateFilter.length > 0 && o.state !== stateFilter) return false;
      if (q.length > 0) {
        return (
          o.name.toLowerCase().includes(q) ||
          o.addressLine1.toLowerCase().includes(q) ||
          o.city.toLowerCase().includes(q) ||
          o.lga.toLowerCase().includes(q) ||
          o.state.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [outlets, search, stateFilter]);

  const canConfirm =
    tracking.trim().length > 0 &&
    selectedOutletId.length > 0 &&
    submit.kind !== 'submitting';

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canConfirm) return;
    setSubmit({ kind: 'submitting' });
    try {
      const result = await requestPickupRedirect(
        tracking.trim().toUpperCase(),
        selectedOutletId,
      );
      setSubmit({ kind: 'success', result });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setSubmit({
            kind: 'error',
            message: 'Tracking number or outlet not found, or you are not the owner.',
          });
        } else if (err.status === 409) {
          setSubmit({
            kind: 'error',
            message: 'Parcel is already delivered or in a terminal status.',
          });
        } else if (err.status === 401 || err.status === 403) {
          setSubmit({
            kind: 'error',
            message: 'Permission denied. Please sign in again.',
          });
        } else {
          setSubmit({ kind: 'error', message: `Redirect failed (${err.status}).` });
        }
      } else {
        setSubmit({ kind: 'error', message: 'Network error. Please try again.' });
      }
    }
  }

  if (submit.kind === 'success') {
    const r = submit.result;
    const matched = outlets.find((o) => o.id === r.outletId);
    return (
      <div>
        <PageHeader
          title="Pickup Location"
          description="Your parcel will be held for pickup at the selected outlet."
        />
        <div className="max-w-2xl mx-auto">
          <div
            className="bg-white rounded-xl border border-border p-8 text-center space-y-4"
            data-testid="pickup-success"
          >
            <CheckCircle2 className="w-12 h-12 text-nipost-green mx-auto" />
            <h2 className="text-xl font-bold text-gray-900">Pickup location confirmed</h2>
            <p className="text-sm text-gray-600">
              Your parcel <span className="font-mono font-semibold">{tracking.trim().toUpperCase()}</span> will be
              held at the outlet below for in-person collection.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2">
              <p className="text-sm font-semibold text-gray-900">{r.outletName}</p>
              {matched ? (
                <p className="text-xs text-gray-500">
                  {matched.addressLine1}{matched.city ? `, ${matched.city}` : ''}{matched.state ? `, ${matched.state}` : ''}
                </p>
              ) : null}
              <div className="flex justify-between text-xs pt-2 border-t border-border">
                <span className="text-gray-500">Outlet code</span>
                <span className="font-mono text-gray-900">{r.outletCode}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Reference</span>
                <span className="font-mono text-gray-900" data-testid="pickup-redirect-id">{r.id}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Status</span>
                <span className="font-semibold text-nipost-green">{r.status}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Pickup Location"
        description="Redirect an in-flight parcel to a NIPOST outlet for in-person collection."
      />

      <div className="max-w-3xl mx-auto space-y-6">
        <form
          onSubmit={(e) => { void handleSubmit(e); }}
          className="bg-white rounded-xl border border-border p-6 space-y-5"
          noValidate
        >
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Pick an outlet</h2>
          </div>

          <div>
            <label htmlFor="ps-tracking" className="block text-sm font-medium text-gray-700 mb-1">
              Tracking number <span className="text-danger">*</span>
            </label>
            <input
              id="ps-tracking"
              type="text"
              value={tracking}
              onChange={(e) => setTracking(e.target.value.toUpperCase())}
              placeholder="e.g. EE123456785NG"
              className={`${inputClass} font-mono`}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label htmlFor="ps-search" className="block text-sm font-medium text-gray-700 mb-1">
                Search outlets
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input
                  id="ps-search"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, LGA, or city"
                  className={`${inputClass} pl-9`}
                />
              </div>
            </div>
            <div>
              <label htmlFor="ps-state" className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <select
                id="ps-state"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className={inputClass}
              >
                <option value="">All states</option>
                {allStates.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          {outletsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500" role="status">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading outlets…
            </div>
          ) : outletsError ? (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" role="alert">
              Could not load outlets: {outletsError}
            </div>
          ) : (
            <div
              className="space-y-2 max-h-96 overflow-y-auto pr-1"
              data-testid="outlet-list"
            >
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-500 italic px-2 py-3">No outlets match your filter.</p>
              ) : (
                filtered.map((o) => {
                  const isSelected = selectedOutletId === o.id;
                  return (
                    <div
                      key={o.id}
                      className={`flex items-center justify-between gap-3 p-4 rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-nipost-green bg-nipost-green-light ring-1 ring-nipost-green/20'
                          : 'border-border hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <MapPin className="w-4 h-4 text-nipost-green flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{o.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {o.addressLine1}{o.city ? `, ${o.city}` : ''}{o.state ? `, ${o.state}` : ''}
                          </p>
                          <p className="text-[11px] text-muted mt-0.5">Code: <span className="font-mono">{o.outletCode}</span></p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedOutletId(o.id)}
                        aria-pressed={isSelected}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${
                          isSelected
                            ? 'bg-nipost-green text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {isSelected ? 'Selected' : 'Select'}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {submit.kind === 'error' ? (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800" role="alert">
              <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{submit.message}</span>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!canConfirm}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors ${
              canConfirm
                ? 'bg-nipost-green text-white hover:bg-nipost-green-dark'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submit.kind === 'submitting' ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Confirming…</span>
            ) : (
              <>
                <ArrowRight className="w-5 h-5" /> Confirm pickup location
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
